import React, { useState, useEffect } from 'react';
import {
  CreditCard,
  Edit2,
  Save,
  X,
  ChevronLeft,
  ChevronRight,
  Filter,
  DollarSign,
  Check
} from 'lucide-react';
import api from '../../utils/api';
import { toast } from 'react-hot-toast';

const SubscriptionManagement = () => {
  const [plans, setPlans] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [editingPlan, setEditingPlan] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [activeTab, setActiveTab] = useState('subscriptions');

  useEffect(() => {
    fetchPlans();
    fetchSubscriptions();
  }, []);

  useEffect(() => {
    if (activeTab === 'subscriptions') {
      fetchSubscriptions();
    }
  }, [page, statusFilter, tierFilter]);

  const fetchPlans = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await api.get('/admin/subscription-plans', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlans(response.data);
    } catch (error) {
      toast.error('Failed to load subscription plans');
    }
  };

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        ...(statusFilter && { status: statusFilter }),
        ...(tierFilter && { tier: tierFilter })
      });
      
      const response = await api.get(`/admin/subscriptions?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSubscriptions(response.data.subscriptions);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPlan = (plan) => {
    setEditingPlan(plan.id);
    setEditForm({
      display_name: plan.display_name,
      description: plan.description,
      price_monthly: plan.price_monthly,
      price_quarterly: plan.price_quarterly,
      price_yearly: plan.price_yearly,
      features: plan.features.join('\n'),
      is_active: plan.is_active
    });
  };

  const handleSavePlan = async (planId) => {
    try {
      const token = localStorage.getItem('adminToken');
      await api.put(
        `/admin/subscription-plans/${planId}`,
        {
          ...editForm,
          features: editForm.features.split('\n').filter(f => f.trim())
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Plan updated successfully');
      setEditingPlan(null);
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update plan');
    }
  };

  const handleUpdateSubscriptionStatus = async (subscriptionId, newStatus) => {
    try {
      const token = localStorage.getItem('adminToken');
      await api.put(
        `/admin/subscriptions/${subscriptionId}/status?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(`Subscription ${newStatus}`);
      fetchSubscriptions();
    } catch (error) {
      toast.error('Failed to update subscription');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-500/20 text-green-400',
      paused: 'bg-yellow-500/20 text-yellow-400',
      cancelled: 'bg-red-500/20 text-red-400'
    };
    return styles[status] || styles.active;
  };

  const getTierBadge = (tier) => {
    const styles = {
      free: 'bg-gray-500/20 text-gray-400',
      basic: 'bg-blue-500/20 text-blue-400',
      premium: 'bg-purple-500/20 text-purple-400',
      vip: 'bg-yellow-500/20 text-yellow-400',
      gold: 'bg-yellow-500/20 text-yellow-400'
    };
    return styles[tier] || styles.basic;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Subscription Management</h1>
      </div>

      {/* Tabs */}
      <div className="flex space-x-4 border-b border-gray-700">
        <button
          onClick={() => setActiveTab('subscriptions')}
          className={`pb-4 px-2 font-medium transition-colors ${
            activeTab === 'subscriptions'
              ? 'text-purple-400 border-b-2 border-purple-400'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          User Subscriptions
        </button>
        <button
          onClick={() => setActiveTab('plans')}
          className={`pb-4 px-2 font-medium transition-colors ${
            activeTab === 'plans'
              ? 'text-purple-400 border-b-2 border-purple-400'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Subscription Plans
        </button>
      </div>

      {activeTab === 'plans' ? (
        /* Plans Management */
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <div key={plan.id} className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
              {editingPlan === plan.id ? (
                /* Edit Mode */
                <div className="p-6 space-y-4">
                  <input
                    type="text"
                    value={editForm.display_name}
                    onChange={(e) => setEditForm({...editForm, display_name: e.target.value})}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                    placeholder="Plan Name"
                  />
                  <textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                    className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                    placeholder="Description"
                    rows={2}
                  />
                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-xs text-gray-400">Monthly</label>
                      <input
                        type="number"
                        value={editForm.price_monthly}
                        onChange={(e) => setEditForm({...editForm, price_monthly: parseFloat(e.target.value)})}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400">Quarterly</label>
                      <input
                        type="number"
                        value={editForm.price_quarterly}
                        onChange={(e) => setEditForm({...editForm, price_quarterly: parseFloat(e.target.value)})}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-gray-400">Yearly</label>
                      <input
                        type="number"
                        value={editForm.price_yearly}
                        onChange={(e) => setEditForm({...editForm, price_yearly: parseFloat(e.target.value)})}
                        className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400">Features (one per line)</label>
                    <textarea
                      value={editForm.features}
                      onChange={(e) => setEditForm({...editForm, features: e.target.value})}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
                      rows={4}
                    />
                  </div>
                  <label className="flex items-center space-x-2 text-gray-300">
                    <input
                      type="checkbox"
                      checked={editForm.is_active}
                      onChange={(e) => setEditForm({...editForm, is_active: e.target.checked})}
                      className="rounded"
                    />
                    <span>Active</span>
                  </label>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => setEditingPlan(null)}
                      className="flex-1 py-2 border border-gray-600 text-gray-300 rounded-lg hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => handleSavePlan(plan.id)}
                      className="flex-1 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center justify-center space-x-2"
                    >
                      <Save className="w-4 h-4" />
                      <span>Save</span>
                    </button>
                  </div>
                </div>
              ) : (
                /* View Mode */
                <>
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-white">{plan.display_name}</h3>
                      <button
                        onClick={() => handleEditPlan(plan)}
                        className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                    </div>
                    <p className="text-gray-400 text-sm mb-4">{plan.description}</p>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Monthly</span>
                        <span className="text-white font-medium">{plan.price_monthly} ETB</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Quarterly</span>
                        <span className="text-white font-medium">{plan.price_quarterly} ETB</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-400">Yearly</span>
                        <span className="text-white font-medium">{plan.price_yearly} ETB</span>
                      </div>
                    </div>
                    
                    <div className="space-y-2">
                      {plan.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center space-x-2 text-sm">
                          <Check className="w-4 h-4 text-green-500" />
                          <span className="text-gray-300">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className={`px-6 py-3 border-t border-gray-700 ${plan.is_active ? 'bg-green-500/10' : 'bg-red-500/10'}`}>
                    <span className={`text-sm ${plan.is_active ? 'text-green-400' : 'text-red-400'}`}>
                      {plan.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      ) : (
        /* Subscriptions List */
        <>
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex items-center space-x-2">
              <Filter className="text-gray-400 w-5 h-5" />
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                className="bg-gray-800 border border-gray-700 rounded-lg text-white px-4 py-2"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="paused">Paused</option>
                <option value="cancelled">Cancelled</option>
              </select>
              <select
                value={tierFilter}
                onChange={(e) => { setTierFilter(e.target.value); setPage(1); }}
                className="bg-gray-800 border border-gray-700 rounded-lg text-white px-4 py-2"
              >
                <option value="">All Plans</option>
                <option value="basic">Basic</option>
                <option value="premium">Premium</option>
                <option value="vip">VIP</option>
              </select>
            </div>
            <div className="flex-1" />
            <span className="text-gray-400 self-center">{total} subscriptions</span>
          </div>

          {/* Subscriptions Table */}
          <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-750">
                  <tr className="text-left text-gray-400 text-sm">
                    <th className="px-6 py-4">User</th>
                    <th className="px-6 py-4">Plan</th>
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4">Billing</th>
                    <th className="px-6 py-4">Period End</th>
                    <th className="px-6 py-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center">
                        <div className="animate-spin w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full mx-auto"></div>
                      </td>
                    </tr>
                  ) : subscriptions.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                        No subscriptions found
                      </td>
                    </tr>
                  ) : (
                    subscriptions.map((sub) => (
                      <tr key={sub.id} className="border-t border-gray-700 hover:bg-gray-750">
                        <td className="px-6 py-4">
                          <div>
                            <p className="text-white font-medium">{sub.user?.name || 'Unknown'}</p>
                            <p className="text-gray-400 text-sm">{sub.user?.phone_number}</p>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs capitalize ${getTierBadge(sub.tier)}`}>
                            {sub.tier}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(sub.status)}`}>
                            {sub.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-gray-300 capitalize">{sub.billing_cycle}</td>
                        <td className="px-6 py-4 text-gray-400 text-sm">
                          {new Date(sub.current_period_end).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            {sub.status === 'active' && (
                              <>
                                <button
                                  onClick={() => handleUpdateSubscriptionStatus(sub.id, 'paused')}
                                  className="px-3 py-1 text-sm bg-yellow-600/20 text-yellow-400 rounded hover:bg-yellow-600/30"
                                >
                                  Pause
                                </button>
                                <button
                                  onClick={() => handleUpdateSubscriptionStatus(sub.id, 'cancelled')}
                                  className="px-3 py-1 text-sm bg-red-600/20 text-red-400 rounded hover:bg-red-600/30"
                                >
                                  Cancel
                                </button>
                              </>
                            )}
                            {sub.status === 'paused' && (
                              <button
                                onClick={() => handleUpdateSubscriptionStatus(sub.id, 'active')}
                                className="px-3 py-1 text-sm bg-green-600/20 text-green-400 rounded hover:bg-green-600/30"
                              >
                                Resume
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-gray-700 flex items-center justify-between">
              <p className="text-gray-400 text-sm">
                Showing {(page - 1) * 20 + 1} to {Math.min(page * 20, total)} of {total}
              </p>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white disabled:opacity-50"
                >
                  <ChevronLeft className="w-5 h-5" />
                </button>
                <span className="text-white px-4">{page} / {totalPages || 1}</span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages || totalPages === 0}
                  className="p-2 hover:bg-gray-700 rounded-lg text-gray-400 hover:text-white disabled:opacity-50"
                >
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SubscriptionManagement;
